import { useCallback } from "react";
import Close from "@mui/icons-material/Close";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import type { IncidentForm, IncidentWorkItemId } from "@thalamos/vision-common";
import { tk } from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import { Banner } from "../../components/Banner/Banner.js";
import { PopupDialog } from "../../components/Dialog/PopupDialog.js";
import { TypographyI18N } from "../../components/index.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { PolicePresenceLogEntryCard } from "../../pages/PolicePresenceLogPage/PolicePresenceLogPage.js";

export interface PolicePresenceUpdatesModalProps {
  open: boolean;
  incidentId: IncidentWorkItemId;
  close: () => void;
  notificationId: string;
}

export type PolicePresenceUpdatesModalInnerProps = {
  open: boolean;
  close: () => void;
  policePresenceForms: IncidentForm[];
  markAsRead: () => void;
};

export function PolicePresenceUpdatesModalInner({
  open,
  close,
  policePresenceForms,
  markAsRead,
}: PolicePresenceUpdatesModalInnerProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Stack sx={{ width: "100%", padding: 2 }} spacing={2}>
      <PopupDialog
        open={open}
        onClose={close}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
        disableDialogRootPadding
      >
        <DialogTitle sx={{ fontSize: "1.250rem", fontWeight: "bold" }}>
          {t("policePresenceNotification.requested")}
          <IconButton
            aria-label="close"
            onClick={close}
            edge="end"
            sx={{ position: "absolute", right: 15, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TypographyI18N
            sx={{ mb: 2 }}
            translationKey={tk(
              "policePresenceNotification.submittedInformation",
            )}
          />
          {[...policePresenceForms]
            .sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            )
            .map((form) => (
              <PolicePresenceLogEntryCard key={form.id} form={form} />
            ))}
          <Banner
            bannerType={"error"}
            title={tk("policePresenceNotification.safetyMessage")}
            body={tk("policePresenceNotification.safetyMessageBody")}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-end", px: 3 }}>
          <Button
            onClick={() => {
              markAsRead();
              close();
            }}
            variant="contained"
          >
            {t("policePresenceNotification.okay")}
          </Button>
        </DialogActions>
      </PopupDialog>
    </Stack>
  );
}

export function PolicePresenceUpdatesModal({
  incidentId,
  open,
  close,
  notificationId,
}: PolicePresenceUpdatesModalProps) {
  const { t } = useTranslation();

  const toastNotifications = useToastNotifications();
  const [{ data: incidentWorkItem }] =
    apiHooks.incidentWorkItem.get(incidentId);

  const policePresenceForms = (incidentWorkItem?.forms ?? []).filter(
    (form) => form.formType === "requestPolicePresence",
  );

  const markAsRead = useCallback(() => {
    void api.notifications.markAsRead(notificationId).catch(() => {
      toastNotifications.show(t("notifications.failedMarkAsRead"), {
        severity: "error",
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationId]);

  if (!incidentWorkItem || policePresenceForms.length === 0) return <></>;

  return (
    <PolicePresenceUpdatesModalInner
      open={open}
      close={close}
      policePresenceForms={policePresenceForms}
      markAsRead={markAsRead}
    />
  );
}
